import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, BookOpen, Briefcase, Calendar, ChevronRight, Download, ExternalLink, Gauge, Shield, Target, Trophy, UserCheck, Users, Zap } from 'lucide-react';
import { api } from '../api';
import { ContentSyndicationModal } from '../components/modals/ContentSyndicationModal';
import { renderStars } from '../utils/renderStars';
import { PrivateWorkshopRequestModal } from '../components/modals/PrivateWorkshopModal';

const Dashboard = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [trainerStats, setTrainerStats] = useState(null);
  const [capacityRules, setCapacityRules] = useState([]);
  const [showSyndicationModal, setShowSyndicationModal] = useState(false);
  const [showPrivateWorkshopModal, setShowPrivateWorkshopModal] = useState(false);
  const canAccessPartnerRequests = ['admin', 'ms_stakeholder', 'partner_admin'].includes(user.role);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (['admin', 'ms_stakeholder'].includes(user.role)) {
          const data = await api.request('/analytics/overview');
          setStats(data);
        }
        if (user.role === 'training_partner') {
          const data = await api.request('/analytics/my-trainer-stats');
          setTrainerStats(data);
        }
        if (user.role === 'admin') {
          const rules = await api.request('/admin/access-rules');
          setCapacityRules(rules.filter(r => !!r.max_users));
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, [user.role]);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-xl shadow-md p-4 card-hover border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-xs">{label}</p>
          <p className="text-2xl font-bold text-levelup-dark mt-0.5">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
          <Icon style={{ color }} size={20} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Welcome Section */}
      <div className="bg-levelup-dark rounded-xl p-3 text-white">
        <h1 className="text-lg font-bold">{t('dashboard.heroTitle')}</h1>
        <p className="text-gray-300 mt-0.5 text-sm">
          {t('dashboard.heroSubtitle')}
        </p>
      </div>

      {/* Our Skilling Pillars */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <h2 className="text-base font-semibold text-levelup-dark mb-3">{t('dashboard.skillingPillars')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="flex items-start space-x-2.5 p-3 bg-gray-50 rounded-lg">
            <div className="w-9 h-9 rounded-lg bg-levelup-dark flex items-center justify-center flex-shrink-0">
              <Award className="text-levelup-accent" size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-levelup-dark">{t('dashboard.certReady')}</h3>
              <p className="text-sm text-gray-500">{t('dashboard.certReadyDesc')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2.5 p-3 bg-gray-50 rounded-lg">
            <div className="w-9 h-9 rounded-lg bg-levelup-dark flex items-center justify-center flex-shrink-0">
              <Users className="text-levelup-accent" size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-levelup-dark">{t('dashboard.salesReady')}</h3>
              <p className="text-sm text-gray-500">{t('dashboard.salesReadyDesc')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2.5 p-3 bg-gray-50 rounded-lg">
            <div className="w-9 h-9 rounded-lg bg-levelup-dark flex items-center justify-center flex-shrink-0">
              <Briefcase className="text-levelup-accent" size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-levelup-dark">{t('dashboard.techDealReady')}</h3>
              <p className="text-sm text-gray-500">{t('dashboard.techDealReadyDesc')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2.5 p-3 bg-gray-50 rounded-lg">
            <div className="w-9 h-9 rounded-lg bg-levelup-dark flex items-center justify-center flex-shrink-0">
              <Target className="text-levelup-accent" size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-levelup-dark">{t('dashboard.projectReady')}</h3>
              <p className="text-sm text-gray-500">{t('dashboard.projectReadyDesc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats for Admin/MS Stakeholder */}
      {['admin', 'ms_stakeholder'].includes(user.role) && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard icon={BookOpen} label={t('dashboard.totalCourses')} value={stats.total_courses} color="#0D3B36" />
          <StatCard icon={Users} label={t('dashboard.totalPartners')} value={stats.total_partners} color="#C4D600" />
          <StatCard icon={Download} label={t('dashboard.totalDownloads')} value={stats.total_downloads} color="#9333EA" />
          <StatCard icon={Calendar} label={t('dashboard.totalWorkshops')} value={stats.total_workshops} color="#F97316" />
          <StatCard icon={UserCheck} label={t('dashboard.trainedLearners')} value={stats.total_trained_learners} color="#EC4899" />
        </div>
      )}

      {/* Seat Capacity mini-widget — admin only, shown when at least one Access Rule has a Max Users cap */}
      {user.role === 'admin' && capacityRules.length > 0 && (
        <div
          data-testid="seats-capacity-widget"
          onClick={() => onNavigate('access-control')}
          className="bg-white rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4"
          style={{ borderLeftColor: '#6366F1' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-levelup-dark flex items-center gap-2">
              <Gauge size={18} className="text-indigo-500" /> Seat Capacity
            </h2>
            <span className="text-xs text-levelup-teal flex items-center gap-1">
              Manage in Access Control <ChevronRight size={13} />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {capacityRules.map(rule => {
              const used = rule.seats_used || 0;
              const pct = Math.min(100, Math.round((used / rule.max_users) * 100));
              const isFull = used >= rule.max_users;
              const isNearFull = !isFull && pct >= 90;
              const barColor = isFull ? '#DC2626' : isNearFull ? '#F59E0B' : '#6366F1';
              return (
                <div key={rule.id} data-testid={`seats-widget-rule-${rule.id}`} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-medium text-gray-800 truncate pr-2">{rule.name}</p>
                    <span className="text-xs font-bold text-gray-600 whitespace-nowrap">{used} / {rule.max_users}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                  </div>
                  {!!rule.waitlist_count && (
                    <p className="text-xs text-amber-600 font-medium mt-1.5 flex items-center gap-1">
                      <Users size={11} /> {rule.waitlist_count} waiting for a seat
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats for Training Partner */}
      {user.role === 'training_partner' && trainerStats && (
        <div>
          <h2 className="text-base font-semibold text-levelup-dark mb-3 flex items-center gap-2">
            <Zap size={18} className="text-levelup-accent" /> Your Training Impact
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-levelup-dark cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('my-analytics')} data-testid="trainer-tile-workshops">
              <p className="text-gray-500 text-xs">Workshops Delivered</p>
              <p className="text-3xl font-bold text-levelup-dark mt-1">{trainerStats.completed_workshops || 0}</p>
              <p className="text-xs text-levelup-teal mt-1">Click to view analytics →</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-levelup-teal cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('my-analytics')} data-testid="trainer-tile-learners">
              <p className="text-gray-500 text-xs">Learners Trained</p>
              <p className="text-3xl font-bold text-levelup-dark mt-1">{trainerStats.total_learners_trained || 0}</p>
              <p className="text-xs text-levelup-teal mt-1">Click to view analytics →</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-400 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('my-analytics')} data-testid="trainer-tile-feedback">
              <p className="text-gray-500 text-xs">Avg Feedback Score</p>
              <p className="text-3xl font-bold text-levelup-dark mt-1">{trainerStats.avg_feedback_score || 0}<span className="text-base text-gray-400">/5</span></p>
              <p className="text-xs text-levelup-teal mt-1">Click to view analytics →</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onNavigate('my-analytics')} data-testid="trainer-tile-impact">
              <p className="text-gray-500 text-xs">Impact Score</p>
              <p className="text-3xl font-bold text-levelup-dark mt-1">{trainerStats.impact_score || 0}</p>
              <div className="flex mt-1">{renderStars(trainerStats.stars || 0)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Current Skilling Offerings */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <h2 className="text-base font-bold text-levelup-dark mb-1">{t('dashboard.currentOfferings')}</h2>
        <p className="text-gray-600 mb-3 text-sm">{t('dashboard.currentOfferingsDesc')}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Train-the-Trainer Resources (Technical, Sales/ Pre-Sales) - Combined */}
          <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors border border-gray-200">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-lg bg-levelup-accent flex items-center justify-center flex-shrink-0">
                <BookOpen className="text-levelup-darker" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-levelup-dark text-base">{t('dashboard.workshopResources')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('dashboard.workshopResourcesDesc')}
                </p>
                <button 
                  onClick={() => onNavigate('courses')}
                  className="mt-2.5 inline-flex items-center px-3 py-1.5 bg-levelup-accent text-levelup-darker rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors text-sm"
                  data-testid="browse-courses-cta"
                >
                  {t('dashboard.browseCourses')}
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
            </div>
          </div>

          {/* Deal Ready Accelerators */}
          <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors border border-gray-200">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-lg bg-levelup-accent flex items-center justify-center flex-shrink-0">
                <Briefcase className="text-levelup-darker" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-levelup-dark text-base">{t('dashboard.dealAccelerators')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('dashboard.dealAcceleratorsDesc')}
                </p>
                <a 
                  href="https://skillupwithlevelup.com/dealreadyaccelerators"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2.5 inline-flex items-center px-3 py-1.5 bg-levelup-accent text-levelup-darker rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors text-sm"
                  data-testid="deal-ready-cta"
                >
                  {t('dashboard.exploreAccelerators')}
                  <ChevronRight size={16} className="ml-1" />
                </a>
              </div>
            </div>
          </div>

          {/* Syndicated Skilling Content — Partner Admin, Admin & MS Stakeholder only */}
          {canAccessPartnerRequests && (
            <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-levelup-accent flex items-center justify-center flex-shrink-0">
                  <Shield className="text-levelup-darker" size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-levelup-dark text-base">{t('dashboard.scormIntegration')}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('dashboard.scormDesc')}
                  </p>
                  <button
                    onClick={() => setShowSyndicationModal(true)}
                    className="mt-2.5 inline-flex items-center px-3 py-1.5 bg-levelup-accent text-levelup-darker rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors text-sm"
                    data-testid="syndication-request-cta"
                  >
                    {t('common.requestNow')}
                    <ChevronRight size={16} className="ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Microsoft Hackathon Content */}
          <div className="bg-levelup-dark rounded-lg p-3 border border-levelup-teal hover:border-levelup-accent transition-colors">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-levelup-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="text-levelup-accent" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white text-base">Microsoft Hackathon Content</h3>
                <p className="text-sm text-gray-300 mt-1">
                  Get ready to code, collaborate, and compete alongside the brightest minds in tech. Dive into immersive, hands-on sessions led by Microsoft experts and challenge your skills with the latest AI and cloud technologies.
                </p>
                <a
                  href="https://microsoftpartnerhackathons.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2.5 inline-flex items-center px-3 py-1.5 bg-levelup-accent text-levelup-darker rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors text-sm"
                  data-testid="hackathon-cta"
                >
                  Explore more
                  <ExternalLink size={14} className="ml-2" />
                </a>
              </div>
            </div>
          </div>

          {/* Request a Private Workshop — Partner Admin, Admin & MS Stakeholder only */}
          {canAccessPartnerRequests && (
            <div className="bg-levelup-dark rounded-lg p-3 border border-levelup-teal hover:border-levelup-accent transition-colors col-span-1 md:col-span-2 lg:col-span-2">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-levelup-accent flex items-center justify-center flex-shrink-0">
                    <Trophy className="text-levelup-darker" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white text-base">Request a Private Workshop</h3>
                    <p className="text-sm text-gray-300 mt-1">
                      Need a tailored workshop experience for your team or resellers? Browse the full LevelUp course catalog and request a private, customised delivery — adapted to your learner profile, solution area focus, and preferred timeframe.
                    </p>
                    <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                      <button
                        onClick={() => setShowPrivateWorkshopModal(true)}
                        className="inline-flex items-center px-3 py-1.5 bg-levelup-accent text-levelup-darker rounded-lg font-medium hover:bg-levelup-accent-hover transition-colors text-sm"
                        data-testid="request-private-workshop-cta"
                      >
                        Request a Private Workshop
                        <ChevronRight size={16} className="ml-1" />
                      </button>
                      <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer"
                        className="text-levelup-accent text-sm underline hover:text-white transition-colors">
                        Browse full catalog →
                      </a>
                    </div>
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Syndication Request Modal */}
      {canAccessPartnerRequests && showSyndicationModal && (
        <ContentSyndicationModal onClose={() => setShowSyndicationModal(false)} />
      )}

      {/* Private Workshop Request Modal */}
      {canAccessPartnerRequests && showPrivateWorkshopModal && (
        <PrivateWorkshopRequestModal user={user} onClose={() => setShowPrivateWorkshopModal(false)} />
      )}
    </div>
  );
};

export default Dashboard;
